import React, { type ReactElement, memo, useEffect, useMemo, useRef, useState } from 'react'
import {
  ClickAwayListener,
  Drawer,
  Grow,
  IconButton,
  Paper,
  Popper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material'

import { Icon } from '~components/internal'
import { lightTheme } from '~createTheme'

export interface TableHeader<T> {
  id?: string
  label?: React.ReactNode
  tooltipInfo?: string
  sortable?: boolean
  render?: (row: T) => React.ReactNode
  width?: string
  minWidth?: string
  maxWidth?: string
  sortDirection?: 'asc' | 'desc' | 'asc-only'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface CustomTableProps<T extends Record<string, any>> {
  headers?: TableHeader<T>[]
  data?: T[]
  loading: boolean
  withDrawer?: boolean
  stickyFirstColumn?: boolean
  noHover?: boolean
  ignoreMaxHeight?: boolean
  drawerContent?: React.ComponentType<{ row: T | null }>
  onRowClick?: (row: T, event: React.MouseEvent<Element, MouseEvent>) => void
  rowMenuContent?: (row: T, closeMenu: () => void) => React.ReactNode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  drawerProps?: Record<string, any>
  verticalAlign?: 'top' | 'middle' | 'bottom'
  onSortChange?: (property: keyof T, order: 'asc' | 'desc') => void
  externalSort?: boolean
  selectedRows?: Set<string>
  inline?: boolean
  sx?: React.CSSProperties
  showHeaders?: boolean
  defaultRows?: number
  rowMenuVisible?: (row: T) => boolean
}

const MemoizedDrawerContent = memo(
  <T,>({
    DrawerContent,
    row,
    ...props
  }: {
    DrawerContent: React.ComponentType<{ row: T | null }>
    row: T | null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } & Record<string, any>) => (DrawerContent ? <DrawerContent row={row} {...props} /> : null),
) as <T>(
  props: {
    DrawerContent: React.ComponentType<{ row: T | null }>
    row: T | null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } & Record<string, any>,
) => JSX.Element | null

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTable<T extends Record<string, any>>({
  headers,
  data,
  loading,
  withDrawer = false,
  stickyFirstColumn = false,
  noHover = false,
  ignoreMaxHeight = false,
  drawerContent: DrawerContent,
  onRowClick,
  rowMenuContent,
  drawerProps = {},
  verticalAlign = 'middle',
  onSortChange,
  selectedRows = new Set(),
  inline = false,
  sx: customSx,
  defaultRows = 5,
  rowMenuVisible,
  externalSort = false,
}: CustomTableProps<T>): ReactElement {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<T | null>(null)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof T | ''>('')
  const [_hoveredRow, setHoveredRow] = useState<number | null>(null)
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const [tableHeight, setTableHeight] = useState<number>(0)

  // Default to one column if no headers provided
  const effectiveHeaders = headers || [
    {
      id: '' as keyof T,
      label: '',
      sortable: false,
      render: undefined,
      width: undefined,
      minWidth: undefined,
      maxWidth: undefined,
      tooltipInfo: undefined,
      sortDirection: undefined,
    },
  ]

  // Default to empty rows if no data provided
  const effectiveData = data || Array.from({ length: defaultRows }, () => ({}) as T)

  const sortedData = useMemo(() => {
    if (!orderBy || externalSort) return effectiveData

    return [...effectiveData].sort((a, b) => {
      const aValue = a[orderBy as keyof T]
      const bValue = b[orderBy as keyof T]

      if (aValue < bValue) return order === 'asc' ? -1 : 1
      if (aValue > bValue) return order === 'asc' ? 1 : -1
      return 0
    })
  }, [effectiveData, order, orderBy, externalSort])

  const handleRequestSort = (property: keyof T): void => {
    const header = effectiveHeaders.find((h) => h.id === property)
    const isAsc = orderBy === property && order === 'asc'
    const newOrder = header?.sortDirection === 'asc-only' ? 'asc' : isAsc ? 'desc' : 'asc'
    setOrder(newOrder)
    setOrderBy(property)
    if (onSortChange) {
      onSortChange(property, newOrder)
    }
  }

  const handleRowClick = (row: T, e: React.MouseEvent): void => {
    if (onRowClick) {
      onRowClick(row, e)
    }
    if (withDrawer) {
      setSelectedRow(row)
      setDrawerOpen(true)
    }
  }

  const handleDrawerClose = (): void => {
    setDrawerOpen(false)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: T): void => {
    event.stopPropagation()
    const isSameAnchor = anchorEl === event.currentTarget

    if (isSameAnchor && menuOpen) {
      setMenuOpen(false)
      setAnchorEl(null)
      setSelectedRow(null)
      return
    }

    setSelectedRow(row)
    setAnchorEl(event.currentTarget)
    setMenuOpen(true)
  }

  const handleMenuClose = (): void => {
    setMenuOpen(false)
    setAnchorEl(null)
  }

  useEffect(() => {
    const updateTableHeight = (): void => {
      if (tableRef.current) {
        setTableHeight(window.innerHeight - tableRef.current.getBoundingClientRect().top + 35)
      }
    }

    updateTableHeight()
    window.addEventListener('resize', updateTableHeight)

    return (): void => {
      window.removeEventListener('resize', updateTableHeight)
    }
  }, [])

  return (
    <TableContainer
      sx={{
        ...(ignoreMaxHeight ? { maxHeight: '100%' } : { maxHeight: `${tableHeight}px` }),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        ...(inline
          ? {
              overflowY: 'visible',
              overflowX: 'clip',
            }
          : {}),
        ...customSx,
      }}
    >
      <Table stickyHeader ref={tableRef}>
        {headers && headers.length > 0 && (
          <TableHead sx={{ position: 'sticky', zIndex: 2 }}>
            <TableRow
              sx={{
                '& th': {
                  borderBottom: `1px solid ${lightTheme.palette.text.primary}`,
                },
              }}
            >
              {effectiveHeaders.map((header, index) => (
                <TableCell
                  key={(header.id as string) || `column-${index}`}
                  component='th'
                  scope='col'
                  sortDirection={orderBy === header.id ? order : false}
                  sx={{
                    textTransform: 'uppercase',
                    ...lightTheme.typography.label,
                    width: header.width,
                    minWidth: header.minWidth,
                    maxWidth: header.maxWidth,
                    fontSize: '14px',
                    py: 2,
                    '@media (max-width: 1200px)': {
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      fontSize: '13px',
                    },
                    '@media (max-width: 900px)': {
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      fontSize: '12px',
                    },
                    ...(stickyFirstColumn &&
                      index === 0 && {
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        minWidth: '180px',
                        backgroundColor: lightTheme.palette.background.paper,
                        borderRight: `3px solid ${lightTheme.palette.divider}`,
                      }),
                  }}
                >
                  <Tooltip title={header.tooltipInfo} placement='top'>
                    {header.sortable && header.id ? (
                      <TableSortLabel
                        active={orderBy === header.id}
                        direction={orderBy === header.id ? order : 'asc'}
                        onClick={() => handleRequestSort(header.id as keyof T)}
                        sx={{ fontSize: 'inherit' }}
                      >
                        {header.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 'inherit',
                        }}
                      >
                        {header.label}
                      </Typography>
                    )}
                  </Tooltip>
                </TableCell>
              ))}
              {rowMenuContent && <TableCell component='th' scope='col' />}
            </TableRow>
          </TableHead>
        )}
        <TableBody sx={{ flexGrow: 1, overflow: 'auto' }}>
          {loading
            ? Array.from({ length: defaultRows }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {effectiveHeaders.map((header, index) => (
                    <TableCell
                      key={(header.id as string) || `column-${index}`}
                      sx={{
                        verticalAlign,
                        width: header.width,
                        minWidth: header.minWidth,
                        maxWidth: header.maxWidth,
                      }}
                    >
                      <Skeleton variant='text' />
                    </TableCell>
                  ))}
                  {rowMenuContent && (
                    <TableCell sx={{ verticalAlign }}>
                      <Skeleton variant='text' />
                    </TableCell>
                  )}
                </TableRow>
              ))
            : sortedData.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  onClick={(e) => handleRowClick(row, e)}
                  onMouseEnter={() => setHoveredRow(rowIndex)}
                  onMouseLeave={() => setHoveredRow(null)}
                  sx={{
                    cursor: withDrawer || onRowClick ? 'pointer' : 'default',
                    '& td': {
                      borderBottom: `0.5px solid ${lightTheme.palette.divider}`,
                      verticalAlign,
                    },
                    backgroundColor: selectedRows?.has(row.id) ? 'var(--paleblue)' : 'inherit',
                    ...(!noHover && {
                      '&:hover': {
                        backgroundColor: lightTheme.palette.action.hover,
                      },
                      '&:hover .more-icon': {
                        opacity: 1,
                      },
                    }),
                  }}
                >
                  {effectiveHeaders.map((header, index) => (
                    <TableCell
                      key={(header.id as string) || `column-${index}`}
                      sx={{
                        verticalAlign,
                        width: header.width,
                        minWidth: header.minWidth,
                        maxWidth: header.maxWidth,
                        ...(stickyFirstColumn &&
                          index === 0 && {
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            minWidth: '180px',
                            backgroundColor: selectedRows?.has(row.id)
                              ? 'var(--paleblue)'
                              : lightTheme.palette.background.paper,
                            borderRight: `3px solid ${lightTheme.palette.divider}`,
                          }),
                      }}
                    >
                      {header.render
                        ? header.render(row)
                        : header.id && row[header.id] !== undefined
                          ? row[header.id]
                          : ''}
                    </TableCell>
                  ))}
                  {rowMenuContent && (
                    <TableCell padding='none' align='right' sx={{ verticalAlign }}>
                      {(!rowMenuVisible || rowMenuVisible(row)) && (
                        <IconButton
                          className='more-icon'
                          size='small'
                          onClick={(e) => handleMenuOpen(e, row)}
                          sx={{ opacity: 0 }}
                        >
                          <Icon name='moreVert' />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
        </TableBody>
      </Table>
      {withDrawer && DrawerContent && (
        <Drawer
          anchor='right'
          open={drawerOpen}
          onClose={handleDrawerClose}
          sx={{
            '& .MuiDrawer-paper': {
              padding: lightTheme.spacing(2),
            },
          }}
        >
          <MemoizedDrawerContent DrawerContent={DrawerContent} row={selectedRow} {...drawerProps} />
        </Drawer>
      )}
      {rowMenuContent && (
        <Popper
          open={menuOpen}
          anchorEl={anchorEl}
          placement='bottom-end'
          transition
          disablePortal
          style={{ position: 'fixed' }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: 'top right' }}>
              <Paper>
                <ClickAwayListener onClickAway={handleMenuClose}>
                  <div>{selectedRow && rowMenuContent(selectedRow, handleMenuClose)}</div>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </TableContainer>
  )
}

export default CustomTable
